import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CHARACTER_OPTIONS, CharacterId } from './characters';
import mainMenuButtonSrc from '../assets/menu/button_main.png';
import topLeftOverlaySrc from '../assets/menu/top_left.png';
import topRightOverlaySrc from '../assets/menu/top_right.png';

type Props = {
  initialId?: CharacterId;
  onConfirm: (id: CharacterId) => void;
  onBack?: () => void;
};

const STORAGE_KEY = 'deckdelve.selectedCharacter.v1';

function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(b, n));
}

type AspectMap = Partial<Record<CharacterId, string>>;


function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const m = window.matchMedia(query);

    const onChange = () => setMatches(m.matches);
    onChange();

    if (m.addEventListener) m.addEventListener('change', onChange);
    else m.addListener(onChange);

    return () => {
      if (m.removeEventListener) m.removeEventListener('change', onChange);
      else m.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

export default function CharacterSelectScreen({
  initialId = 'fate',
  onConfirm,
}: Props) {
  const options = useMemo(() => CHARACTER_OPTIONS, []);
  const reduceMotion = useReducedMotion();
  const isMobile = useMediaQuery('(max-width: 640px)');

  const initialIndex = useMemo(() => {
    const idx = options.findIndex((o) => o.id === initialId);
    return idx >= 0 ? idx : 0;
  }, [options, initialId]);

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const [activeIndex, setActiveIndex] = useState<number>(initialIndex);
  const [sidePaddingPx, setSidePaddingPx] = useState<number>(24);
  const rafRef = useRef<number | null>(null);

  // Image aspect ratio per choice (so we never crop/distort — we scale the whole card instead).
  const [aspectById, setAspectById] = useState<AspectMap>({});

  const handleImageLoad = useCallback((id: CharacterId, img: HTMLImageElement) => {
    const w = img.naturalWidth || 0;
    const h = img.naturalHeight || 0;
    if (!w || !h) return;

    const ratio = `${w} / ${h}`;
    setAspectById((prev) => (prev[id] === ratio ? prev : { ...prev, [id]: ratio }));
  }, []);

  const active = options[activeIndex] ?? options[0];

  const scrollToIndex = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth') => {
      const scroller = scrollerRef.current;
      const el = cardRefs.current[index];
      if (!scroller || !el) return;

      const scrollerRect = scroller.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();

      const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;
      const elCenter = elRect.left + elRect.width / 2;

      const delta = elCenter - scrollerCenter;
      scroller.scrollTo({ left: scroller.scrollLeft + delta, behavior });
    },
    []
  );

  const computeNearestIndex = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;

    let bestIdx = 0;
    let bestDist = Number.POSITIVE_INFINITY;

    for (let i = 0; i < options.length; i++) {
      const el = cardRefs.current[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      const c = r.left + r.width / 2;
      const d = Math.abs(c - scrollerCenter);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }

    setActiveIndex(bestIdx);
  }, [options.length]);

  const onScroll = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;
      computeNearestIndex();
    });
  }, [computeNearestIndex]);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const first = cardRefs.current[0];
    if (!scroller || !first) return;

    const measure = () => {
      const scrollerW = scroller.getBoundingClientRect().width;
      const cardW = first.getBoundingClientRect().width;
      const pad = Math.max(16, Math.floor((scrollerW - cardW) / 2));
      setSidePaddingPx(pad);
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    ro.observe(scroller);
    ro.observe(first);

    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    // Snap to initial index on first render.
    scrollToIndex(initialIndex, 'auto');
    setActiveIndex(initialIndex);
  }, [initialIndex, scrollToIndex]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        const next = clamp(activeIndex - 1, 0, options.length - 1);
        scrollToIndex(next);
      }
      if (e.key === 'ArrowRight') {
        const next = clamp(activeIndex + 1, 0, options.length - 1);
        scrollToIndex(next);
      }
      if (e.key === 'Enter') {
        if (!active.locked) onConfirm(active.id);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    active.id,
    active.locked,
    activeIndex,
    onConfirm,
    options.length,
    scrollToIndex,
  ]);

  const confirm = () => {
    if (active.locked) return;
    try {
      localStorage.setItem(STORAGE_KEY, active.id);
    } catch {}
    onConfirm(active.id);
  };


  const renderCornerOverlay = (src: string, side: 'left' | 'right') => {
    const overlayWidth = isMobile ? 'min(76vw, 520px)' : 'min(50vw, 720px)';
    const maskPosition = side === 'left' ? 'top left' : 'top right';
    const shimmerTravel = { backgroundPosition: ['50% 135%', '50% -35%'] };

    const maskStyle = {
      WebkitMaskImage: `url(${src})`,
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskSize: 'contain',
      WebkitMaskPosition: maskPosition,
      maskImage: `url(${src})`,
      maskRepeat: 'no-repeat',
      maskSize: 'contain',
      maskPosition,
    } as React.CSSProperties;

    return (
      <div
        className={`absolute bottom-0 ${side === 'left' ? 'left-0' : 'right-0'} select-none pointer-events-none`}
        style={{
          width: overlayWidth,
          transform: 'scaleY(-1)',
          transformOrigin: 'center center',
        }}
      >
        <img
          src={src}
          alt=""
          aria-hidden
          draggable={false}
          className="block w-full h-auto select-none pointer-events-none"
        />

        {!reduceMotion && (
          <>
            <motion.div
              className="absolute inset-0 pointer-events-none"
              animate={{ opacity: [0.14, 0.26, 0.15] }}
              transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                ...maskStyle,
                background:
                  'linear-gradient(180deg, rgba(255,120,20,0.10) 0%, rgba(255,140,35,0.18) 28%, rgba(255,185,90,0.34) 62%, rgba(255,140,35,0.14) 100%)',
                mixBlendMode: 'screen',
                filter: 'blur(1.2px) saturate(1.3)',
              }}
            />

            <motion.div
              className="absolute inset-0 pointer-events-none"
              animate={{
                ...shimmerTravel,
                opacity: [0, 0.9, 0],
              }}
              transition={{
                duration: 3.6,
                repeat: Infinity,
                ease: 'linear',
                repeatDelay: 0.8,
              }}
              style={{
                ...maskStyle,
                background:
                  'linear-gradient(180deg, transparent 28%, rgba(255,155,45,0) 40%, rgba(255,227,150,1) 50%, rgba(255,155,45,0) 60%, transparent 72%)',
                backgroundSize: '170% 250%',
                mixBlendMode: 'screen',
                filter: 'blur(1.7px) drop-shadow(0 0 8px rgba(255,185,90,0.65))',
              }}
            />

            <motion.div
              className="absolute inset-0 pointer-events-none"
              animate={{ opacity: [0.02, 0.1, 0.03] }}
              transition={{ duration: 5.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                ...maskStyle,
                background: 'rgba(255,150,40,0.85)',
                mixBlendMode: 'screen',
                filter: 'blur(8px)',
              }}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div className="relative min-h-[100svh] w-full bg-zinc-950 text-zinc-100 overflow-x-hidden overflow-y-auto">
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        {renderCornerOverlay(topLeftOverlaySrc, 'left')}
        {renderCornerOverlay(topRightOverlaySrc, 'right')}
      </div>

      <div className="relative z-10 flex min-h-[100svh] flex-col">
        <div className="px-6 pt-14 pb-4 sm:px-10 text-center"
        style={{ fontFamily: 'Karma, ui-serif, Georgia, serif' }}>
        <h1 className="mt-2 text-3xl sm:text-4xl">
          Choose your path
        </h1>
      </div>

      <div className="flex-1 flex flex-col justify-center py-2">
        <div className="relative">
          <div
            ref={scrollerRef}
            onScroll={onScroll}
            className="flex gap-4 sm:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar"
            style={{
              paddingLeft: sidePaddingPx,
              paddingRight: sidePaddingPx,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {options.map((opt, i) => {
              const isActive = i === activeIndex;

              // We scale the card size (via responsive height) but never force the art to "cover",
              // so you don't lose top/bottom on different screens.
              const aspect = aspectById[opt.id] ?? '3 / 5';

              return (
                <button
                  key={opt.id}
                  ref={(el) => {
                    cardRefs.current[i] = el;
                  }}
                  type="button"
                  className={[
                    'snap-center shrink-0',
                    'w-auto',
                    'rounded-[20px]',
                    'border border-zinc-800',
                    'bg-zinc-900/60',
                    'shadow-lg',
                    'relative overflow-hidden',
                    'transition-transform duration-200',
                    // Important: don't "grow" the active card beyond its base size on small screens
                    // (that’s what made it feel *more* cropped). Instead, slightly shrink the non-active ones.
                    isActive
                      ? 'scale-100 origin-center opacity-100'
                      : 'scale-[0.96] opacity-90 hover:opacity-100',
                  ].join(' ')}
                  style={{
                    height: 'clamp(280px, 54svh, 560px)',
                    aspectRatio: aspect,
                  }}
                  onClick={() => scrollToIndex(i)}
                >
                  <div className="absolute inset-0 bg-zinc-950/40">
                    {opt.imageSrc ? (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img
                          src={opt.imageSrc}
                          alt={opt.title}
                          draggable={false}
                          onLoad={(e) => handleImageLoad(opt.id, e.currentTarget)}
                          className="w-full h-full object-contain opacity-95"
                        />
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-b from-zinc-800/70 to-zinc-950/70" />
                    )}
                  </div>



                  {opt.locked && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="rounded-xl bg-black/55 border border-zinc-700 px-4 py-3 text-sm">
                        Play to unlock!
                      </div>
                    </div>
                  )}

                  <div
                    className={[
                      'absolute inset-0 rounded-2xl pointer-events-none',
                      isActive ? 'ring-2 ring-zinc-200/40' : 'ring-0',
                    ].join(' ')}
                  />
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex items-center justify-center gap-2">
            {options.map((_, i) => (
              <div
                key={i}
                className={[
                  'h-2 rounded-full transition-all',
                  i === activeIndex ? 'w-6 bg-zinc-200/70' : 'w-2 bg-zinc-600/60',
                ].join(' ')}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="px-6 pb-8 sm:px-10 flex items-center justify-between gap-4">


        <div className="w-full flex justify-center">
          <button
            type="button"
            onClick={confirm}
            disabled={active.locked}
            aria-disabled={active.locked}
            className="relative inline-block p-0 m-0 bg-transparent border-0 outline-none"
            style={{
              WebkitTapHighlightColor: 'transparent',
              backgroundColor: 'transparent',
              lineHeight: 0,
              opacity: active.locked ? 0.6 : 1,
              cursor: active.locked ? 'default' : 'pointer',
            }}
          >
            <img
              src={mainMenuButtonSrc}
              alt=""
              draggable={false}
              className="block h-auto select-none pointer-events-none"
              style={{
                width: 'clamp(170px, 18vw, 230px)',
                filter: active.locked ? 'grayscale(1) brightness(0.6)' : 'none',
              }}
            />

            <span
              className="absolute inset-0 flex items-center justify-center select-none pointer-events-none"
              style={{
                fontFamily: 'Karma, ui-serif, Georgia, serif',
                fontSize: 'clamp(16px, 1.2vw, 22px)',
                textShadow: '0 2px 6px rgba(0,0,0,0.85)',
                letterSpacing: '0.01em',
                opacity: active.locked ? 0.72 : 1,
                color: active.locked ? 'rgba(210,210,210,0.9)' : '#f1ede3',
                transform: 'translateY(2px)',
              }}
            >
              Accept
            </span>
          </button>
        </div>
      </div>

        <style>{`
          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>
      </div>
    </div>
  );
}
